import Form from 'components/Form';

import { Fragment, useState } from 'react';
import { AccessTime, Delete } from '@mui/icons-material';
import { Button, Grid, IconButton, MenuItem, Box } from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers';

import { Ti18nId } from 'hooks/useI18n';
import { IGlucoseRecord } from 'apis/survey/type';
import { IFormRegister, IFormValues, IFormWatch } from 'routes/Main/type';
import MuiTable from 'components/MuiTable';
import MuiTextField from 'components/Form/MuiTextField';
import SectionTitle from '../components/SectionTitle';

import { formatStringToDate } from 'utils/formatting';

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
  onRequired: (id: Ti18nId) => void;
  onSuccess: (message: string) => void;
}

const GlucoseRecords = (props: Props) => {
  const { disabled, watch, setValue, onRequired, onSuccess, register } = props;
  const glucoseRecordList: IGlucoseRecord[] = watch('blood_sugar_log');

  const [date, setDate] = useState('');
  const [time, setTime] = useState(null);
  const [meal, setMeal] = useState('');
  const [mealEtc, setMealEtc] = useState('');
  const [item, setItem] = useState('');
  const [itemEtc, setItemEtc] = useState('');
  const [result, setResult] = useState('');

  const columns = [
    { fieldId: 'date', label: 'Date', sx: { width: 200 } },
    { fieldId: 'time', label: 'Time', sx: { width: 200 } },
    { fieldId: 'activity', label: 'Meal', sx: { width: 200 } },
    { fieldId: 'category', label: 'Type', sx: { width: 200 } },
    { fieldId: 'level', label: 'Blood Sugar Level', sx: { width: 400 } },
    { fieldId: 'action', label: '', sx: { width: 100 } },
  ];

  const meals = ['before meal', 'after meal', 'Others (direct input)'];
  const items = [
    'Glucose(blood sugar)',
    'FBS',
    'PP2',
    'HbA1c',
    'Others (direct input)',
  ];

  const onAddRow = () => {
    const request = {
      date,
      time,
      activity: meal === '기타(직접입력)' ? mealEtc : meal,
      category: item === '기타(직접입력)' ? itemEtc : item,
      level: result,
    };

    if (Object.values(request).filter(v => !v).length > 0) {
      return onRequired('REQUIRED.NONCHOICE');
    }

    onSuccess('Added successfully');
    setValue(
      'blood_sugar_log',
      glucoseRecordList ? [...glucoseRecordList, request] : [request]
    );
    setValue('glucose_date', '');
    setDate('');
    setTime(null);
    setMeal('');
    setMealEtc('');
    setItem('');
    setItemEtc('');
    setResult('');
  };

  const inputRow = {
    id: 'add-glucose-record',
    date: (
      <Form.MuiTextField
        type="date"
        InputLabelProps={{ shrink: true }}
        required={false}
        disabled={disabled}
        sx={{ width: '170px' }}
        {...register('glucose_date', {
          onChange: e => setDate(e.target.value),
        })}
      />
    ),
    time: (
      <MobileTimePicker
        value={time}
        onChange={setTime}
        renderInput={params => (
          <MuiTextField
            {...params}
            required={false}
            placeholder="00:00"
            InputProps={{ endAdornment: <AccessTime /> }}
          />
        )}
      />
    ),
    activity: (
      <Box sx={{ display: 'flex' }}>
        <MuiTextField
          select
          value={meal}
          required={false}
          onChange={({ target: { value } }) => setMeal(value)}
        >
          {meals.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </MuiTextField>
        {meal === 'Others (direct input)' && (
          <MuiTextField
            value={mealEtc}
            onChange={({ target: { value } }) => setMealEtc(value)}
          />
        )}
      </Box>
    ),
    category: (
      <Box sx={{ display: 'flex' }}>
        <MuiTextField
          select
          value={item}
          required={false}
          onChange={({ target: { value } }) => setItem(value)}
        >
          {items.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </MuiTextField>
        {item === 'Others (direct input)' && (
          <MuiTextField
            value={itemEtc}
            onChange={({ target: { value } }) => setItemEtc(value)}
          />
        )}
      </Box>
    ),
    level: (
      <MuiTextField
        value={result}
        required={false}
        onChange={({ target: { value } }) => setResult(value)}
      />
    ),
    action: (
      <Button variant="contained" size="small" onClick={onAddRow}>
        Add
      </Button>
    ),
  };

  const onDeleteRow = (index: number) => {
    setValue(
      'blood_sugar_log',
      glucoseRecordList.filter((_, i) => i !== index)
    );
  };

  const displayRows = glucoseRecordList
    ? glucoseRecordList.map((item, i) => ({
        ...item,
        id: i,
        time: formatStringToDate(item.time, 'hh:mm a'),
        action: (
          <IconButton
            size="small"
            onClick={() => onDeleteRow(i)}
            sx={{ display: disabled ? 'none' : 'block' }}
          >
            <Delete />
          </IconButton>
        ),
      }))
    : [];
  const tableRow = disabled ? displayRows : [inputRow, ...displayRows];

  return (
    <Fragment>
      <SectionTitle title="Blood Sugar Log" />
      <Grid item xs={12}>
        <MuiTable columns={columns} rows={[...tableRow]} />
      </Grid>
    </Fragment>
  );
};

export default GlucoseRecords;
